import React from "react";
import {
  Users,
  Github,
  Linkedin,
  Video,
  Code,
  FlaskConical,
  Activity,
  Cpu,
  Heart,
  Thermometer,
  Zap,
  FileText
} from "lucide-react";
import { Link } from "react-router-dom";
import Navigation from "../components/Navigation";
import FooterSection from "../components/FooterSection";
import "./TeamPage.css";

// --- TEAM REGISTRY DATA ---
const coreDevelopers = [
  {
    id: 1,
    name: "Vivek",
    role: "Full Stack Dev & Lead Software Architect",
    bio: "B.Tech CSE (Blockchain). Leads the full-stack architecture, clinical telemetry systems, and overall platform vision.",
    skills: ["React", "Firebase", "Node.js", "Blockchain", "Full Stack"],
    github: "https://github.com/webdeveloperdesigner",
    linkedin: "https://www.linkedin.com/in/vivek-vns/",
    photo: "https://media.licdn.com/dms/image/v2/D4E35AQEq7-1tEO2AUA/profile-framedphoto-shrink_100_100/B4EZYuD2AcHYAk-/0/1744529482018?e=1778835600&v=beta&t=CoOX-WDh5Ajg15S9IwknL65N2oQzftZ9D-ffcdOOfIw",
    accentColor: "#06b6d4",
    badge: "LEAD ARCHITECT"
  },
  {
    id: 2,
    name: "Vikash Verma",
    role: "IoT Engineer & Project Lead",
    bio: "B.Tech CSE (IIoT). Manages the project lifecycle, sensor firmware, and final project reporting.",
    skills: ["IoT", "ESP32", "Sensors", "Project Management"],
    github: "https://github.com/onlyvikashverma",
    linkedin: "https://www.linkedin.com/in/onlyvikashverma/",
    photo: "https://media.licdn.com/dms/image/v2/D5635AQGE3x3L64lP3w/profile-framedphoto-shrink_800_800/profile-framedphoto-shrink_800_800/0/1735660557100?e=1778835600&v=beta&t=GxvVcVwNlsMK64muFLlerqA1VONVxOzsPSrDxH0ooAY",
    accentColor: "#f59e0b",
    badge: "PROJECT LEAD"
  },
  {
    id: 3,
    name: "Digambar Singh",
    role: "UI/UX Designer",
    bio: "B.Tech CSE. Crafts intuitive, premium clinical interfaces with a focus on user experience and high-fidelity visual design.",
    skills: ["Figma", "UI Design", "UX Research", "CSS3"],
    github: "https://github.com/digambasingh",
    linkedin: "https://www.linkedin.com/in/digambar-singh-969878250/",
    photo: "https://media.licdn.com/dms/image/v2/D5603AQENc9PK1M0dlQ/profile-displayphoto-crop_800_800/B56ZgDHN9kHQAI-/0/1752398881097?e=1779926400&v=beta&t=bEJpwp68ENKiKZmhmmLa6eB4-RD-arCtYg9nwggV_rQ",
    accentColor: "#a855f7",
    badge: "UI/UX"
  },
  {
    id: 4,
    name: "Durgesh Kumar",
    role: "Backend Engineer",
    bio: "B.Tech CSE. Builds server-side logic, database architecture, and real-time data pipelines.",
    skills: ["Node.js", "Firebase", "Database", "Cloud Functions"],
    github: "https://github.com/CSdurgesh66",
    linkedin: "https://www.linkedin.com/in/durgesh-baghel-275aa9295",
    photo: "https://avatars.githubusercontent.com/u/122608416?v=4",
    accentColor: "#10b981",
    badge: "BACKEND"
  },
  {
    id: 5,
    name: "Anil Kumar",
    role: "Backend Engineer",
    bio: "B.Tech CSE. Specializes in authentication flows, data security, and HIPAA-compliant backend systems.",
    skills: ["Node.js", "Firebase", "Security", "REST APIs"],
    github: "#",
    linkedin: "#",
    photo: "https://img.freepik.com/free-vector/user-circles-set_78370-4704.jpg?semt=ais_hybrid&w=740&q=80",
    accentColor: "#10b981",
    badge: "BACKEND"
  },
  {
    id: 6,
    name: "Vedant Sharma",
    role: "IoT Engineer",
    bio: "B.Tech CSE (IIoT). Wires, calibrates, and programs smart sensors for real-time clinical telemetry.",
    skills: ["IoT", "Arduino", "Sensors", "ESP32"],
    github: "#",
    linkedin: "https://www.linkedin.com/in/vedant-sharma-891765220/",
    photo: "https://media.licdn.com/dms/image/v2/D4D03AQG9R4pnAlzmnw/profile-displayphoto-shrink_800_800/profile-displayphoto-shrink_800_800/0/1694344215105?e=1779926400&v=beta&t=unVw3Sg80ZBjdk60TBOqdf14ygbe1vqngOXsnCIICoQ",
    accentColor: "#f59e0b",
    badge: "IOT"
  }
];

const studentContributors = [
  {
    id: 101,
    name: "Digambar Singh",
    type: "UI/UX Design",
    description: "Designed and refined clinical interfaces, visual components, and the high-fidelity design system across all dashboards."
  },
  {
    id: 102,
    name: "Anil Kumar",
    type: "Research Paper & Report Maker",
    description: "Authored the full project research paper and technical reports covering system architecture, IoT integration, and clinical evaluation results."
  },
  {
    id: 103,
    name: "Vedant Sharma",
    type: "IoT Devices",
    description: "Handled hardware setup, sensor calibration, and firmware for MAX30102, AD8232, and DS18B20 real-time monitoring sensors."
  }
];

const researchItems = [
  {
    id: 201,
    topic: "Real-Time Telemetry Latency Study",
    contribution: "Health Data Analysis",
    explanation: "Deep-dive analysis of clinical data packet loss over low-bandwidth MQTT networks, optimizing sub-second reactivity for critical vitals."
  },
  {
    id: 202,
    topic: "Multi-Sensor IoT Interference",
    contribution: "IoT Systems Research",
    explanation: "Explored signal interference between AD8232 and MAX30102 sensors in high-noise clinical environments to ensure cardiac monitoring precision."
  },
  {
    id: 203,
    topic: "Medico.in Project Report & Research Paper",
    contribution: "Documentation & Research",
    explanation: "Compiled the full technical research paper covering system architecture, IoT hardware integration, Firebase backend, and clinical evaluation. Authored by Anil Kumar.",
    link: "/project-report"
  }
];

const researchVideos = [
  {
    id: 301,
    title: "ESP32 — Clinical Compute Node",
    description: "Dual-core MCU powering wireless telemetry at 240MHz over WiFi.",
    icon: Cpu,
    thumbnail: "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=2070&auto=format&fit=crop"
  },
  {
    id: 302,
    title: "MAX30102 — SpO2 & Heart Rate",
    description: "18-bit photoplethysmogram sensor for pulse and blood oxygen.",
    icon: Heart,
    thumbnail: "https://images.unsplash.com/photo-1581092580497-e0d23cbdf1dc?q=80&w=2070&auto=format&fit=crop"
  },
  {
    id: 303,
    title: "AD8232 — ECG Cardiac Monitor",
    description: "Single-lead ECG waveform capture with 80dB CMRR precision.",
    icon: Activity,
    thumbnail: "https://images.unsplash.com/photo-1597589827317-4c6d6e0a90bd?q=80&w=2070&auto=format&fit=crop"
  },
  {
    id: 304,
    title: "DS18B20 — Thermal Node",
    description: "±0.5°C 1-Wire temperature sensor for continuous body monitoring.",
    icon: Thermometer,
    thumbnail: "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=2070&auto=format&fit=crop"
  }
];

const TeamPage = () => {
  return (
    <div className="tp-shell">
      <Navigation />
      <div className="tp-grid-bg" />
      <div className="tp-scanline" />

      {/* ── HERO ── */}
      <header className="tp-hero">
        <div className="tp-hero-inner">
          <div className="tp-eyebrow">
            <Users size={13} />
            <span>PEOPLE_REGISTRY_V2.0</span>
          </div>
          <h1 className="tp-hero-title">
            The Minds Behind<br />
            <span className="tp-gradient">Medico.in</span>
          </h1>
          <p className="tp-hero-sub">
            A cross-disciplinary team of engineers, designers, and researchers building
            the future of real-time clinical intelligence.
          </p>
          <div className="tp-hero-stats">
            <div className="tp-stat"><span>6</span><label>Team Members</label></div>
            <div className="tp-stat-divider" />
            <div className="tp-stat"><span>3</span><label>Specialisations</label></div>
            <div className="tp-stat-divider" />
            <div className="tp-stat"><span>4</span><label>IoT Sensors</label></div>
          </div>
        </div>
      </header>

      <main className="tp-main">

        {/* ── SECTION 1: CORE TEAM ── */}
        <section className="tp-section">
          <div className="tp-section-label">
            <Code size={16} />
            <span>01 / CORE_DEVELOPMENT_TEAM</span>
          </div>

          {/* Featured founder row */}
          {coreDevelopers.filter(d => d.featured || d.id === 1).map(founder => (
            <div key={founder.id} className="tp-founder-card" style={{ "--accent": founder.accentColor }}>
              <div className="tp-founder-img">
                <img src={founder.photo} alt={founder.name} />
                <div className="tp-founder-img-ring" />
              </div>
              <div className="tp-founder-body">
                <span className="tp-role-badge" style={{ borderColor: founder.accentColor, color: founder.accentColor }}>
                  {founder.badge}
                </span>
                <h2>{founder.name}</h2>
                <p className="tp-founder-role">{founder.role}</p>
                <p className="tp-founder-bio">{founder.bio}</p>
                <div className="tp-skills">
                  {founder.skills.map(s => <span key={s} className="tp-skill">{s}</span>)}
                </div>
                <div className="tp-socials">
                  <a href={founder.github} target="_blank" rel="noopener noreferrer" className="tp-social-btn"><Github size={18} /></a>
                  <a href={founder.linkedin} className="tp-social-btn"><Linkedin size={18} /></a>
                </div>
              </div>
            </div>
          ))}

          {/* Team grid */}
          <div className="tp-dev-grid">
            {coreDevelopers.filter(d => d.id !== 1).map(dev => (
              <div key={dev.id} className="tp-dev-card" style={{ "--accent": dev.accentColor }}>
                <div className="tp-dev-top">
                  <div className="tp-dev-img">
                    <img src={dev.photo} alt={dev.name} />
                  </div>
                  <span className="tp-role-badge" style={{ borderColor: dev.accentColor, color: dev.accentColor }}>
                    {dev.badge}
                  </span>
                </div>
                <h3>{dev.name}</h3>
                <p className="tp-dev-role">{dev.role}</p>
                <p className="tp-dev-bio">{dev.bio}</p>
                <div className="tp-skills">
                  {dev.skills.map(s => <span key={s} className="tp-skill">{s}</span>)}
                </div>
                <div className="tp-socials">
                  <a href={dev.github} className="tp-social-btn"><Github size={16} /></a>
                  <a href={dev.linkedin} className="tp-social-btn"><Linkedin size={16} /></a>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── SECTION 2: STUDENT CONTRIBUTORS ── */}
        <section className="tp-section">
          <div className="tp-section-label">
            <Activity size={16} />
            <span>02 / STUDENT_CONTRIBUTORS</span>
          </div>
          <div className="tp-contrib-grid">
            {studentContributors.map((sc, i) => (
              <div key={sc.id} className="tp-contrib-card">
                <div className="tp-contrib-num">0{i + 1}</div>
                <div className="tp-contrib-body">
                  <span className="tp-contrib-type">{sc.type.toUpperCase()}</span>
                  <h3>{sc.name}</h3>
                  <p>{sc.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── SECTION 3: RESEARCH TIMELINE ── */}
        <section className="tp-section">
          <div className="tp-section-label">
            <FlaskConical size={16} />
            <span>03 / RESEARCH_&amp;_DOCUMENTATION</span>
          </div>
          <div className="tp-timeline">
            {researchItems.map((ri, i) => (
              <div key={ri.id} className="tp-timeline-item">
                <div className="tp-timeline-dot">
                  {i === 2 ? <FileText size={14} /> : <Zap size={14} />}
                </div>
                <div className="tp-timeline-card">
                  <span className="tp-timeline-tag">{ri.contribution.toUpperCase()}</span>
                  <h3>{ri.topic}</h3>
                  <p>{ri.explanation}</p>
                  {ri.link && (
                    <Link to={ri.link} style={{ color: "var(--tp-cyan)", fontSize: "0.82rem", fontWeight: "700", textDecoration: "none", marginTop: "0.8rem", display: "inline-block" }}>
                      View Project Report →
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── SECTION 4: IOT DEVICE GALLERY ── */}
        <section className="tp-section">
          <div className="tp-section-label">
            <Video size={16} />
            <span>04 / IIOT_DEVICE_GALLERY</span>
          </div>
          <div className="tp-device-grid">
            {researchVideos.map(device => {
              const Icon = device.icon;
              return (
                <div key={device.id} className="tp-device-card">
                  <div className="tp-device-img">
                    <img src={device.thumbnail} alt={device.title} />
                    <div className="tp-device-overlay">
                      <div className="tp-device-icon-badge"><Icon size={22} /></div>
                    </div>
                  </div>
                  <div className="tp-device-info">
                    <h3>{device.title}</h3>
                    <p>{device.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

      </main>

      <FooterSection />
    </div>
  );
};

export default TeamPage;
